import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { fetchAllCategories } from "../../../core/data/redux/payroll/Accounting/expensesCategories/expensesCategoriesAction";
import { fetchAllBudgets } from "../../../core/data/redux/payroll/Accounting/budgets/budgetsAction";
import { createBudgetExpense, fetchAllBudgetExpenses, updateBudgetExpense } from "../../../core/data/redux/payroll/Accounting/budgetExpenses/budgetExpensesActions";
import { DatePicker, Drawer, message, Select } from "antd";
import { status } from "../../../core/common/selectoption/selectoption";
import dayjs from "dayjs";
import { filterByStatus } from "../../../core/utils/filterActiveData";

interface BudgetExpenseFormData {
  budgetId: string;
  categoryId: string;
  status: string;
  createdBy: number;
  amount: string;
  expenseDate: dayjs.Dayjs | null;
  expenseName: string;
  file: File | null;
}

const BudgetExpensesModal: React.FC<any> = ({ modalType, handleCancel, isModalVisible, selectedRowData, setTableKey }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: any) => state.auth);
  const { budgets } = useSelector((state: any) => state.accounting.budget);
  const { categories } = useSelector((state: any) => state.accounting.expensesCategories);
  const { loadingStates } = useSelector((state: any) => state.accounting.budgetExpenses);
  const isLoading = modalType === "edit" ? loadingStates.update : loadingStates.create;
  const [formData, setFormData] = useState<BudgetExpenseFormData>({
    budgetId: "",
    categoryId: "",
    status: "active",
    createdBy: user?.UserId || 1,
    amount: "",
    expenseDate: null,
    expenseName: "",
    file: null,
  });

  const activeCategories = filterByStatus(categories, "active", "status");
  const activeBudget = filterByStatus(budgets, "active", "status");

  const resetForm = () => {
    setFormData({
      budgetId: "",
      categoryId: "",
      status: "active",
      createdBy: user?.UserId || 1,
      amount: "",
      expenseDate: null,
      expenseName: "",
      file: null,
    });
    setErrors({});
  };

  const [errors, setErrors] = useState<any>({});

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev: any) => ({ ...prev, [field]: "" }));
  };

  useEffect(() => {
    dispatch(fetchAllCategories());
    dispatch(fetchAllBudgets());
    if (modalType === "edit" && selectedRowData) {
      setFormData({
        ...selectedRowData,
        file: selectedRowData.filePath ? { name: selectedRowData.filePath.split("/").pop() } : null,
        expenseDate: selectedRowData.expenseDate ? dayjs(selectedRowData.expenseDate) : null,
      });
    }
  }, [dispatch, modalType, selectedRowData]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      setFormData((prev: any) => ({
        ...prev,
        file: files[0],
      }));
    } else {
      setFormData((prev: any) => ({
        ...prev,
        file: null,
      }));
    }
  };

  const validateForm = () => {
    const newErrors: any = {};
    let isValid = true;

    if (!formData.expenseName.trim()) {
      newErrors.expenseName = "Expense name is required.";
      isValid = false;
    }

    if (!formData.categoryId) {
      newErrors.categoryId = "Category is required.";
      isValid = false;
    }

    if (!formData.budgetId) {
      newErrors.budgetId = "Budget is required.";
      isValid = false;
    }

    if (!formData.amount || isNaN(Number(formData.amount)) || Number(formData.amount) <= 0) {
      newErrors.amount = "Valid positive amount is required.";
      isValid = false;
    }

    if (!formData.expenseDate) {
      newErrors.expenseDate = "Expense date is required.";
      isValid = false;
    }

    if (!formData.status) {
      newErrors.status = "Status is required.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };
  const handleSubmit = async () => {
    if (!validateForm()) return;

    const form = new FormData();
    form.append('budgetId', formData.budgetId);
    form.append('categoryId', formData.categoryId);
    form.append('status', formData.status);
    form.append('createdBy', formData.createdBy.toString());
    form.append('amount', formData.amount);
    form.append('expenseName', formData.expenseName);

    if (formData.expenseDate && dayjs.isDayjs(formData.expenseDate)) {
      form.append('expenseDate', formData.expenseDate.format("YYYY-MM-DD"));
    }

    if (formData.file) {
      form.append('file', formData.file);
    }

    try {
      let result;
      if (modalType === "edit" && selectedRowData) {
        form.append('modifyBy', user?.UserId?.toString() || '1');
        result = await dispatch(updateBudgetExpense({
          id: selectedRowData.budget_expense_id,
          data: form
        })).unwrap();
      } else {
        result = await dispatch(createBudgetExpense(form)).unwrap();
      }
      if (result.status === "success") {
        message.success(modalType === "edit" ? "Budget Expense updated successfully" : "Budget Expense created successfully");
        await dispatch(fetchAllBudgetExpenses());
        setTableKey((prevKey: any) => prevKey + 1);
      }
    } catch (error) {
      message.error("An error occurred while processing your request.");
    } finally {
      handleCancel();
    }
  };

  const getModalContainer = () => document.getElementById("modal-datepicker") || document.body;
  return (
    <Drawer
      title={modalType === "edit" ? "Edit Budget Expense" : "Add Budget Expense"}
      placement="right"
      width={600}
      onClose={() => {
        handleCancel();
        resetForm();
      }}
      visible={isModalVisible}
      style={{ marginTop: 50, paddingBottom: 50 }}
    >
      <form>
        <div className="modal-body pb-0">
          <div className="row">
            <div className="col-md-12">
              <div className="mb-3">
                <label className="form-label">Expense Name</label>
                <input
                  type="text"
                  className={`form-control ${errors.expenseName ? "is-invalid" : ""}`}
                  value={formData.expenseName}
                  onChange={(e) => handleChange('expenseName', e.target.value)}
                />
                {errors.expenseName && <div className="invalid-feedback">{errors.expenseName}</div>}
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Category Name</label>
              <Select
                value={formData.categoryId}
                onChange={(value) => handleChange("categoryId", value)}
                className={`w-100 ${errors.categoryId ? "is-invalid-select" : ""}`}
              >
                {activeCategories?.map((option: any) => (
                  <Select.Option key={option.expenses_categories_id} value={option.expenses_categories_id}>
                    {option.category}
                  </Select.Option>
                ))}
              </Select>
              {errors.categoryId && <div className="text-danger mt-1">{errors.categoryId}</div>}
            </div>
            <div className="mb-3">
              <label className="form-label">Budget</label>
              <Select
                value={formData.budgetId}
                onChange={(value) => handleChange("budgetId", value)}
                className={`w-100 ${errors.budgetId ? "is-invalid-select" : ""}`}
              >
                {activeBudget?.map((option: any) => (
                  <Select.Option key={option.budget_id} value={option.budget_id}>
                    {option.budgetTitle}
                  </Select.Option>
                ))}
              </Select>
              {errors.budgetId && <div className="text-danger mt-1">{errors.budgetId}</div>}
            </div>
            <div className="mb-3">
              <label className="form-label">Amount</label>
              <input
                type="text"
                className={`form-control ${errors.amount ? "is-invalid" : ""}`}
                value={formData.amount}
                onChange={(e) => handleChange('amount', e.target.value)}
              />
              {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}

            </div>
            <div className="mb-3">
              <label className="form-label">Expense Date</label>
              <div className="input-icon-end position-relative">
                <DatePicker
                  value={formData.expenseDate || null}
                  onChange={(date) => handleChange("expenseDate", date)}
                  className={`form-control datetimepicker ${errors.expenseDate ? "is-invalid" : ""}`}
                  placeholder="DD-MM-YYYY"
                  format="DD-MM-YYYY"
                  getPopupContainer={getModalContainer}
                />
                <span className="input-icon-addon">
                  <i className="ti ti-calendar text-gray-7" />
                </span>
              </div>
              {errors.expenseDate && <div className="text-danger mt-1">{errors.expenseDate}</div>}

            </div>
            <div className="mb-3">
              <label className="form-label">Status</label>
              <Select
                value={formData.status}
                onChange={(value) => handleChange("status", value)}
                className={`w-100 ${errors.status ? "is-invalid-select" : ""}`}
              >
                {status.map((option: any) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
              {errors.status && <div className="text-danger mt-1">{errors.status}</div>}
            </div>
            <div className="mb-3">
              {/* <label className="form-label">Upload File</label> */}
              <div className="file-upload position-relative btn btn-sm btn-primary px-3 mb-2">
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleFileChange}
                  style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }}
                />
                <span className="fs-14 text-center mb-2">Choose File</span>
              </div>

              {/* ✅ File name preview */}
              {formData.file && (
                <div className="mt-2">
                  <strong>Selected File:</strong> {formData.file.name}
                </div>
              )}
            </div>

          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-light me-2"
            onClick={() => {
              handleCancel();
              resetForm();
            }}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={isLoading}>
            {isLoading && <span className="spinner-border spinner-border-sm me-2" />}
            {modalType === "edit" ? "Update Budget Expense" : "Add Budget Expense"}
          </button>
        </div>
      </form>
    </Drawer>
  );
};

export default BudgetExpensesModal;
